import React from 'react';
import type { ReactNode } from 'react';
import { translations as fallbackTranslations } from '../utils/index';

type Language = 'fr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  loading: boolean;
  error: string | null;
  refreshTranslations: () => Promise<void>;
}

// Create and export the context
const LanguageContext = React.createContext<LanguageContextType | undefined>(undefined);

//  EXPORT EXPLICITE du context et du type
export { LanguageContext };
export type { LanguageContextType };

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = React.useState<Language>(() => {
    const saved = localStorage.getItem('portfolio-language');
    return (saved === 'en' || saved === 'fr') ? saved : 'fr';
  });

  const [backendTranslations, setBackendTranslations] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  
  // Protection anti-boucle pour éviter les requêtes simultanées
  const loadingRef = React.useRef<boolean>(false);
  const lastLoadTimeRef = React.useRef<number>(0);

  const t = (key: string): string => {
  try {
    // 1. Chercher dans les traductions backend (format plat)
    if (backendTranslations && typeof backendTranslations === 'object') {
      if (Object.prototype.hasOwnProperty.call(backendTranslations, key)) {
        return backendTranslations[key];
      }
    }

  // 2. Look into fallback translations (nested format)
    const keys = key.split('.');

  // fix - Handle typing correctly
    const translations = fallbackTranslations as Record<string, any>;
    let value: any = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }

    return typeof value === 'string' ? value : key;
  } catch (err) {
    return key;
  }
};

  const refreshTranslations = React.useCallback(async () => {
    // Protection anti-requêtes simultanées  
    if (loadingRef.current) {
      console.log('LanguageContext: Requête traduction déjà en cours, ignorée');
      return;
    }
    
    // Throttling: minimum 2 secondes entre les requêtes
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    if (timeSinceLastLoad < 2000) {
      console.log('LanguageContext: Throttling actif, requête ignorée');
      return;
    }
    
    try {
      loadingRef.current = true;
      lastLoadTimeRef.current = now;
      setLoading(true);
      setError(null);

      // Utiliser le service centralisé (cache court + déduplication)
      const { apiService } = await import('../services/apiService');
      const res = await apiService.get<any>(`/translations/${language}`, false, {
        useCache: true,
        cacheTtlMs: 120_000, // 2 min (augmenté)
        dedupe: true,
        timeout: 8000
      });
      if (res.success) {
        const data = res.data as any;
        setBackendTranslations((data as any)?.translations || data);
      } else {
        throw new Error(res.error || 'Erreur chargement traductions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setBackendTranslations(null);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [language]); // ✅ CORRECTION - Dépendance language appropriée

  React.useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  React.useEffect(() => {
    refreshTranslations();
  }, [refreshTranslations]); // ✅ CORRECTION - Dépendance sur la fonction mémorisée

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('portfolio-language', lang);
    window.dispatchEvent(new CustomEvent('languageChange', { detail: lang }));
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    loading,
    error,
    refreshTranslations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
